import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import { ReadonlyObject } from 'schema/schema';
import { httpChecked, queryClient } from 'util/fetch';

const Checker = TypeCompiler.Compile(ReadonlyObject({ count: Type.Integer() }));

async function fetchData(url: string, signal: AbortSignal) {
  const response = await httpChecked.get(url, Checker, { signal });
  return response.count;
}

const counts = {
  base: ['counts'] as const,
  rules: (section_type: string, field_type: string, language_code: string) =>
    queryOptions({
      queryKey: [
        ...counts.base,
        'rules',
        { section_type, field_type, language_code },
      ] as const,
      queryFn: ({ signal }) =>
        fetchData(
          `/rules/${language_code}/${section_type}/${field_type}/count`,
          signal,
        ),
    }),
  rule_suggestions: (
    section_type: string,
    field_type: string,
    language_code: string,
  ) =>
    queryOptions({
      queryKey: [
        ...counts.base,
        'rule_suggestions',
        { section_type, field_type, language_code },
      ] as const,
      queryFn: ({ signal }) =>
        fetchData(
          `/rule_suggestions/${language_code}/${section_type}/${field_type}/count`,
          signal,
        ),
    }),
  masterdata: (section_type: string, field_type: string) => {
    const skip =
      section_type === 'project_experiences' &&
      (field_type === 'customer' || field_type === 'description');

    return queryOptions({
      queryKey: [
        ...counts.base,
        'masterdata',
        { section_type, field_type },
      ] as const,
      queryFn: skip
        ? skipToken
        : ({ signal }) =>
            fetchData(
              `/masterdata/${section_type}/${field_type}/count`,
              signal,
            ),
    });
  },
  unapproved: (
    section_type: string,
    field_type: string,
    language_code: string,
  ) => {
    // If the section_type and field_type are project_experiences and description,
    // we don't want to make a request as they are not supported
    const skip =
      section_type === 'project_experiences' && field_type === 'description';

    return queryOptions({
      queryKey: [
        ...counts.base,
        'unapproved',
        { section_type, field_type, language_code },
      ] as const,
      queryFn: skip
        ? skipToken
        : ({ signal }) =>
            fetchData(
              `/unapproved/${language_code}/${section_type}/${field_type}/count`,
              signal,
            ),
    });
  },
};

export function useRulesCount(
  section_type: string,
  field_type: string,
  language_code: string,
) {
  return useQuery({
    ...counts.rules(section_type, field_type, language_code),
    staleTime: 60_000,
  });
}

export function useRuleSuggestionsCount(
  section_type: string,
  field_type: string,
  language_code: string,
) {
  return useQuery({
    ...counts.rule_suggestions(section_type, field_type, language_code),
    staleTime: 60_000,
  });
}

export function useMasterdataCount(section_type: string, field_type: string) {
  return useQuery({
    ...counts.masterdata(section_type, field_type),
    staleTime: 60_000,
  });
}

export function useUnapprovedCount(
  section_type: string,
  field_type: string,
  language_code: string,
) {
  return useQuery({
    ...counts.unapproved(section_type, field_type, language_code),
    staleTime: 60_000,
  });
}

export async function invalidateRulesCount(
  section_type: string,
  field_type: string,
  language_code: string,
) {
  await queryClient.invalidateQueries(
    counts.rules(section_type, field_type, language_code),
  );
}

export async function invalidateRuleSuggestionsCount(
  section_type: string,
  field_type: string,
  language_code: string,
) {
  await queryClient.invalidateQueries(
    counts.rule_suggestions(section_type, field_type, language_code),
  );
}

export async function invalidateUnapprovedCount(
  section_type: string,
  field_type: string,
  language_code: string,
) {
  await queryClient.invalidateQueries(
    counts.unapproved(section_type, field_type, language_code),
  );
}

export async function invalidateMasterdataCount(
  section_type: string,
  field_type: string,
) {
  await queryClient.invalidateQueries(
    counts.masterdata(section_type, field_type),
  );
}
